import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Copy, ExportSquare, Refresh, Setting4, TickCircle } from 'iconsax-react';
import { copyCurrentUrl } from '../../lib/webPush';
import { SeqRing } from './IosInstallGuide';

/**
 * 스레드 앱 안 브라우저(아이폰) 전용 안내 (대표 2026-10-30 "5번은 무조건 고쳐야 돼").
 *
 * 스레드 안에는 '홈 화면에 추가'가 없어 설치 안내를 따라 할 수 없고, 자동 이동(x-safari) 단추도
 * 메타 앱 안에서는 거의 안 먹는다(10/30 클래리티: 인스타 앱 안 149번 중 5초 안 이동 10번).
 * 스레드 ⋯ 메뉴의 '외부 브라우저에서 열기'가 사파리를 여는 것은 대표 아이폰(Threads 453)에서 확인했다.
 * 그 길만 그림으로 알려 주고, 메뉴를 못 찾는 손님을 위해 링크 복사를 둔다.
 * 그림의 메뉴 줄 순서와 글자는 그 실기기 화면 그대로다. 로그인은 이어 주지 않는다(사파리에서 다시 로그인).
 */

const MenuRow: React.FC<{ icon: React.ReactNode; label: string; active?: boolean; reduce: boolean }> = ({ icon, label, active, reduce }) => (
    <div className={`relative flex items-center gap-2.5 border-t border-[#3a3d44] px-3 py-2 first:border-t-0 ${active ? 'font-semibold text-white' : 'text-[#8a8f98]'}`}>
        {icon}
        <span>{label}</span>
        {active && <SeqRing reduce={reduce} phase="b" rounded="rounded-none" />}
    </div>
);

/** 스레드 안 브라우저 맨 위 줄 → ⋯ → 메뉴. 대표 아이폰 실제 화면을 옮긴 그림 */
const ThreadsMenuVisual: React.FC<{ reduce: boolean }> = ({ reduce }) => (
    <div aria-hidden className="overflow-hidden rounded-2xl border border-[#262626] bg-[#0F0F0F] pb-3 text-[12px]">
        <div className="flex items-center justify-between border-b border-[#262626] px-3 py-2 text-[#A8A8A8]">
            <span className="w-7 text-[13px]">✕</span>
            <span className="flex flex-col items-center leading-tight">
                <span className="text-[11px] font-medium text-white">socialdataai.com</span>
                <span className="text-[10px] text-[#777777]">Threads</span>
            </span>
            <span className="relative flex h-7 w-7 items-center justify-center rounded-full font-bold text-white">
                ⋯
                <SeqRing reduce={reduce} phase="a" rounded="rounded-full" />
            </span>
        </div>
        <div className="ml-auto mr-2 mt-2 w-[210px] overflow-hidden rounded-2xl border border-[#3a3d44] bg-[#26282e] shadow-[0_16px_40px_rgba(0,0,0,0.7)]">
            <MenuRow reduce={reduce} icon={<Refresh size={14} variant="Linear" style={{ color: 'currentColor' }} />} label="새로 고침" />
            <MenuRow reduce={reduce} icon={<Setting4 size={14} variant="Linear" style={{ color: 'currentColor' }} />} label="브라우저 설정" />
            <MenuRow reduce={reduce} active icon={<ExportSquare size={14} variant="Linear" style={{ color: 'currentColor' }} />} label="외부 브라우저에서 열기" />
            <MenuRow reduce={reduce} icon={<Copy size={14} variant="Linear" style={{ color: 'currentColor' }} />} label="링크 복사" />
        </div>
    </div>
);

interface ThreadsSafariGuideProps {
    /** 3단계 — 사파리에 간 뒤 할 일 한 줄 */
    nextStep: React.ReactNode;
    /** 복사할 주소. 없으면 지금 화면 주소 */
    copyHref?: string;
    /** 복사 단추 아래 한 줄 */
    copyHint: string;
}

export const ThreadsSafariGuide: React.FC<ThreadsSafariGuideProps> = ({ nextStep, copyHref, copyHint }) => {
    const reduce = useReducedMotion() ?? false;
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        const ok = await copyCurrentUrl(copyHref);
        setCopied(ok);
        if (ok) window.setTimeout(() => setCopied(false), 4000);
    };

    const steps: React.ReactNode[] = [
        <>오른쪽 위 <span className="font-semibold text-white">점 세 개(⋯)</span>를 눌러요</>,
        <><span className="font-semibold text-white">외부 브라우저에서 열기</span>를 눌러요</>,
        nextStep,
    ];

    return (
        <div className="space-y-3">
            <ThreadsMenuVisual reduce={reduce} />

            <ol className="space-y-2 text-sm leading-relaxed text-[#A8A8A8]">
                {steps.map((text, index) => (
                    <li key={index} className="flex gap-2.5">
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#3182f6] text-[11px] font-bold text-white">
                            {index + 1}
                        </span>
                        <span>{text}</span>
                    </li>
                ))}
            </ol>

            <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={handleCopy}
                className={`inline-flex w-full items-center justify-center gap-1.5 rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${
                    copied
                        ? 'border-[#00D87E]/40 text-[#00D87E]'
                        : 'border-[#262626] text-[#A8A8A8] hover:text-white'
                }`}
            >
                {copied ? (
                    <>
                        <TickCircle size={16} variant="Bold" style={{ color: '#00D87E' }} />
                        복사됐어요
                    </>
                ) : (
                    <>
                        <Copy size={16} variant="Linear" style={{ color: 'currentColor' }} />
                        링크 복사
                    </>
                )}
            </motion.button>
            <p className="text-center text-[11px] leading-relaxed text-[#777777]">{copyHint}</p>
        </div>
    );
};

export default ThreadsSafariGuide;
